/** @file Rewrites mesh-space deform offsets of a skinned mesh as per-influence bone-local offsets (§6.7). */
import type { MeshAtt } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';

type Skinned = Extract<MeshAtt['geometry'], { kind: 'skinned' }>;

const IDENTITY = [1, 0, 0, 1, 0, 0];

/** Number of influences of the vertices before `vertex` (the compiled index of its first influence). */
function influenceStart(g: Skinned, vertex: number): number {
  let n = 0;
  for (let v = 0; v < vertex; v++) n += g.weights[v]?.length ?? 0;
  return n;
}

/**
 * `offsets` covers vertices `start/2 …` (2 numbers each, bind space). Each vertex offset δ becomes
 * `Lin(inverse(bindPose[b])) · δ` for every influence b, in compileSkinning's influence order;
 * `start` becomes 2 × the index of the range's first influence (§6.14).
 */
export function deformToInfluences(g: Skinned, inverse: ReadonlyMap<string, Affine>, offsets: { start: number; values: readonly number[] }): { start: number; values: number[] } {
  const first = offsets.start / 2;
  const linearOf = (bone: string): Affine => inverse.get(bone) ?? IDENTITY;
  const values: number[] = [];
  const d: [number, number] = [0, 0];
  for (let k = 0; 2 * k < offsets.values.length; k++) {
    const dx = offsets.values[2 * k] ?? 0;
    const dy = offsets.values[2 * k + 1] ?? 0;
    for (const [bone] of g.weights[first + k] ?? []) {
      affine.applyVec(linearOf(bone), dx, dy, d);
      values.push(d[0], d[1]);
    }
  }
  return { start: 2 * influenceStart(g, first), values };
}
